// src/components/InvoiceDetailModal.tsx
import React from 'react';
import { X, AlertTriangle, RefreshCw } from 'lucide-react';
import { SalesInvoice, SalesInvoiceItem } from '../types';

interface InvoiceDetailModalProps {
  invoice: SalesInvoice | null;
  items: SalesInvoiceItem[];
  loading?: boolean;
  canViewCost?: boolean;
  onClose: () => void;
}

export const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  invoice,
  items,
  loading = false,
  canViewCost = false,
  onClose,
}) => {
  if (!invoice) return null;

  const formatAED = (val?: number) => {
    if (val === undefined || val === null) return '—';
    return `AED ${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const renderStatusBadge = (status: string) => {
    const s = status.toLowerCase();
    let bg = 'bg-slate-100 text-slate-800 border-slate-200';
    if (s === 'paid') bg = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (s === 'unpaid') bg = 'bg-amber-50 text-amber-700 border-amber-200';
    if (s === 'overdue') bg = 'bg-rose-50 text-rose-700 border-rose-200';
    if (s === 'cancelled') bg = 'bg-slate-100 text-slate-500 border-slate-300 line-through';
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider border ${bg}`}>
        {status}
      </span>
    );
  };

  const renderGpBadge = (margin?: number) => {
    if (margin === undefined || margin === null) return null;
    let color = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (margin < 0) color = 'bg-rose-50 text-rose-700 border-rose-200';
    else if (margin < 20) color = 'bg-amber-50 text-amber-700 border-amber-200';
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-bold border ${color}`}>
        {margin > 0 ? '+' : ''}{margin.toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 bg-slate-900 text-white flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">Invoice Details</span>
              <span className="text-xs bg-slate-800 text-slate-300 px-2 py-0.5 rounded font-mono">
                {invoice.invoice_date}
              </span>
              {renderStatusBadge(invoice.status)}
              {Boolean(invoice.has_active_negative_stock) && (
                <span className="inline-flex items-center gap-1 text-rose-300 bg-rose-950/80 border border-rose-700 px-2 py-0.5 rounded-full text-[10px] font-bold">
                  <AlertTriangle className="w-2.5 h-2.5 text-rose-400" />
                  Negative Stock
                </span>
              )}
            </div>
            <h3 className="text-xl font-bold font-mono mt-1 text-white">{invoice.invoice_no}</h3>
            <p className="text-xs text-slate-300 mt-0.5">
              Customer: <strong className="text-white">{invoice.customer_name}</strong> • Sales Rep: <strong className="text-white">{invoice.sales_person}</strong>
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Line Items Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Cancelled Notice */}
          {invoice.status === 'cancelled' && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3.5 flex items-center gap-3 text-xs text-rose-800">
              <div className="p-1.5 bg-rose-100 rounded-lg text-rose-700 flex-shrink-0">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div>
                <strong className="font-bold">This invoice is Cancelled.</strong> It is kept in system records for audit compliance, but its figures are excluded from company GP and salesperson performance metrics.
              </div>
            </div>
          )}

          {/* Negative Stock Notice */}
          {Boolean(invoice.has_active_negative_stock) && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-center gap-3 text-xs text-amber-900">
              <div className="p-1.5 bg-amber-100 rounded-lg text-amber-700 flex-shrink-0">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div>
                <strong className="font-bold">Billed with Negative Stock:</strong> This invoice was generated when physical inventory was insufficient (stock was 0 or in deficit). Once Kumar Limbu or the factory logs the corresponding production batch, this alert will automatically clear.
              </div>
            </div>
          )}

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
            <div>
              <span className="text-xs text-slate-500 font-semibold block uppercase">Total Billed</span>
              <span className="text-lg font-bold text-slate-900 font-mono">{formatAED(invoice.total_sold_amount)}</span>
            </div>
            {canViewCost && (
              <div>
                <span className="text-xs text-slate-500 font-semibold block uppercase">Total Base Cost</span>
                <span className="text-lg font-bold text-slate-700 font-mono">{formatAED(invoice.total_base_cost)}</span>
              </div>
            )}
            {canViewCost && (
              <div>
                <span className="text-xs text-slate-500 font-semibold block uppercase">Gross Profit</span>
                <span className="text-lg font-bold text-emerald-700 font-mono">{formatAED(invoice.total_gp_amount)}</span>
              </div>
            )}
            {canViewCost && (
              <div>
                <span className="text-xs text-slate-500 font-semibold block uppercase">GP Margin</span>
                <div className="mt-0.5">{renderGpBadge(invoice.gp_margin_pct)}</div>
              </div>
            )}
          </div>

          {/* Line Items Table */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-slate-900 text-sm">Line Items & Valuation Breakdown</h4>
              <span className="text-xs text-slate-500">{items.length} line item(s)</span>
            </div>

            {loading ? (
              <div className="text-center py-10 bg-slate-50 rounded-lg border border-slate-200 text-slate-400">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto text-emerald-600 mb-2" />
                <span className="text-xs">Loading line items...</span>
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100 text-slate-600 font-semibold">
                    <tr>
                      <th className="p-3">ERP Item Description</th>
                      <th className="p-3">Catalog Matched Product</th>
                      <th className="p-3 text-right">Qty</th>
                      <th className="p-3 text-right">Sold Rate</th>
                      <th className="p-3 text-right">Total Billed</th>
                      {canViewCost && <th className="p-3 text-right">Base Cost</th>}
                      {canViewCost && <th className="p-3 text-right">GP (AED)</th>}
                      {canViewCost && <th className="p-3 text-right">GP %</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.length > 0 ? (
                      items.map((item) => (
                        <tr key={item.id} className="hover:bg-slate-50">
                          <td className="p-3 font-medium text-slate-800">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span>{item.item_name}</span>
                              {Boolean(item.had_negative_stock) && item.current_stock_balance !== undefined && item.current_stock_balance < 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap">
                                  <AlertTriangle className="w-2.5 h-2.5 text-rose-600" />
                                  Deficit ({item.current_stock_balance} units)
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-emerald-700 font-semibold">
                            {item.catalog_product_name || (
                              <span className="text-amber-600 italic">Unmatched</span>
                            )}
                          </td>
                          <td className="p-3 text-right font-medium">{item.qty} {item.uom}</td>
                          <td className="p-3 text-right font-mono">{formatAED(item.sold_rate)}</td>
                          <td className="p-3 text-right font-bold text-slate-900 font-mono">{formatAED(item.sold_amount)}</td>
                          {canViewCost && <td className="p-3 text-right text-slate-600 font-mono">{formatAED(item.total_base_cost)}</td>}
                          {canViewCost && (
                            <td className="p-3 text-right font-semibold text-emerald-700 font-mono">
                              {formatAED(item.gp_amount)}
                            </td>
                          )}
                          {canViewCost && <td className="p-3 text-right">{renderGpBadge(item.gp_margin_pct)}</td>}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={canViewCost ? 8 : 5} className="text-center py-6 text-slate-400">
                          No line items found for this invoice.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold transition"
          >
            Close Details
          </button>
        </div>
      </div>
    </div>
  );
};
